import { getNodeType } from "@/common";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import type { SearchResult as SearchDocumentsSearchResult } from "@/components/search/search-documents/Search.types";
import SearchAndSelectResultItem from "@/components/search/search-documents/SearchAndSelectResultItem";
import { isStrNotNullOrBlank } from "@/components/util/UtilString";
import { isStrNullOrBlank } from "@/components/util/UtilString";
import { trimDocName } from "@/hooks/documents";
import Select from "@imminently/decisively-ui/Select";
import { type ReleaseDocument, findReleaseDocument, normaliseText, normalizeText } from "@packages/commons";
import debounce from "lodash/debounce";
import React from "react";
import { useTranslation } from "react-i18next";

const getNormalisedDocumentName = (
  doc: ReleaseDocument & {
    normalisedName?: string;
  },
) => {
  if (!doc.normalisedName) {
    doc.normalisedName = normaliseText(doc.name);
  }
  return doc.normalisedName;
};

interface SearchRuleGraphNodesProps {}

const SearchRuleGraphNodes = (props: SearchRuleGraphNodesProps) => {
  const { graph, setSelectedNodeId, debug, graphVizType, restrictedView, setRestrictedViewRootPath } =
    useGraphVisualisation();
  const release = useFullRelease();
  /** where [] is "no results" and null is "no search performed yet" */
  const [searchText, setLocalSearchText] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<SearchDocumentsSearchResult[] | null>([]);
  const allowHiddenNodesInResults = !(debug ?? false);
  const { t } = useTranslation();
  const isGraphFocusSearch = debug && (graphVizType === "classic" || graphVizType === "classic-beta");
  const focusedNodeId = isGraphFocusSearch ? restrictedView?.rootPath : null;
  const focusedNode = focusedNodeId ? graph?.node(focusedNodeId) : null;
  // -- search

  const searchNodes_ = React.useCallback((srchStr: string) => {
    if (!graph || !graph.nodes || !release) return;
    const lcSearch = normalizeText(srchStr.toLowerCase());
    const newValues: SearchDocumentsSearchResult[] = [];
    // @ts-ignore
    for (const id of graph.nodes()) {
      const node = graph.node(id);
      if (
        node &&
        (allowHiddenNodesInResults || (!allowHiddenNodesInResults && !node.hidden)) &&
        node.description &&
        node.description.toLowerCase().indexOf(lcSearch) > -1 &&
        !node.fk
      ) {
        // Process node data similar to SearchDocuments
        const fullDocPathNoExt: string = trimDocName(node.definedIn || "");
        const documentName = fullDocPathNoExt.substring(fullDocPathNoExt.lastIndexOf("/") + 1);
        const documentPath = fullDocPathNoExt.substring(0, fullDocPathNoExt.lastIndexOf("/"));

        const isInput = isStrNullOrBlank(node.definedIn || "");
        const document = isInput
          ? null
          : findReleaseDocument(release, (doc) => getNormalisedDocumentName(doc) === fullDocPathNoExt);

        const referencedBy = (node.usedIn || [])
          .map((usedIn: any) => {
            const usedInPathNoExt = trimDocName(usedIn);
            const doc = findReleaseDocument(release, (doc) => getNormalisedDocumentName(doc) === usedInPathNoExt);
            return doc;
          })
          .filter(Boolean) as ReleaseDocument[];

        const processedNode = {
          v: node.id,
          ...node,
          documentName: documentName || "",
          documentPath: documentPath || "/",
          documentId: document?.reference || "",
          document,
          referencedBy,
          isInput,
          nodeType: getNodeType(node),
        };

        newValues.push({
          index: 0,
          score: 1,
          string: node.description,
          original: processedNode,
        });
      }
    }
    setSearchResults(newValues);
  }, [allowHiddenNodesInResults, graph, release]);

  const searchNodesDebounced = React.useMemo(() => debounce(searchNodes_, 200), [searchNodes_]);

  const searchNodes = () => {
    // @ts-ignore
    if (isStrNotNullOrBlank(searchText) && graph && graph.nodes && graph.nodes().length > 0) {
      searchNodesDebounced(searchText);
    }
  };

  React.useEffect(() => {
    if (isStrNotNullOrBlank(searchText)) {
      searchNodes();
    } else {
      searchNodesDebounced.cancel();
      setSearchResults(null);
    }
    return () => searchNodesDebounced.cancel();
  }, [searchText, graph, searchNodesDebounced]);

  // Convert search results to Select options
  const selectOptions = React.useMemo(() => {
    const options = (searchResults || []).map((result) => ({
      value: "path" in result.original && result.original.path ? result.original.path : result.original.id,
      label: result.string,
      searchResult: result, // Store the full result for custom rendering
    }));

    if (focusedNodeId && focusedNode && !searchText && !options.some((option) => option.value === focusedNodeId)) {
      options.unshift({
        value: focusedNodeId,
        label: focusedNode.description || focusedNodeId,
        searchResult: null as any,
      });
    }

    return options;
  }, [focusedNode, focusedNodeId, searchResults, searchText]);

  // -- clear/reset

  const onClear = () => {
    searchNodesDebounced.cancel();
    setLocalSearchText("");
    setSearchResults(null);
  };

  // -- navigation

  const selectNodeOnGraph = (nodeId: string | null) => {
    if (!nodeId) {
      if (isGraphFocusSearch) {
        setRestrictedViewRootPath(null);
      }
      return;
    }

    if (nodeId) {
      setSelectedNodeId(nodeId);
      const node = graph.node(nodeId);
      if (node) {
        setSelectedNodeId(nodeId);
        if (isGraphFocusSearch) {
          // TODO do we want to make this the behavior for all modes?
          setRestrictedViewRootPath(node.path || node.id || nodeId);
        }
      }
      setLocalSearchText("");
      setSearchResults(null);
    }
  };

  const handleInputChange = (inputValue: string) => {
    setLocalSearchText(inputValue);
  };

  // Custom Option component that renders the full SearchAndSelectResultItem
  const CustomOption = (props: any) => {
    const { data, innerRef, innerProps } = props;
    const searchResult = data.searchResult as SearchDocumentsSearchResult;

    if (!searchResult) {
      // Fallback to default rendering
      return (
        <div
          ref={innerRef}
          {...innerProps}
          className="px-2 py-1"
        >
          {data.label}
        </div>
      );
    }

    const isRelationship = searchResult.original.nodeType === "relationship";
    const entityName = isRelationship ? "relationship" : (searchResult.original as any).entity;
    const type = isRelationship ? "relationship" : (searchResult.original as any).type;

    // Extract onClick from innerProps and ensure it's called when clicking anywhere in the option
    const { onClick, ...restInnerProps } = innerProps;

    return (
      <div
        ref={innerRef}
        {...restInnerProps}
        onClick={(e) => {
          // Call react-select's onClick handler
          if (onClick) {
            onClick(e);
          }
        }}
        style={{ padding: 0, cursor: "pointer" }}
      >
        <SearchAndSelectResultItem
          result={searchResult}
          additionalInfo={{
            entityName,
            type,
            matchingText: searchResult.string,
            highlightStr: normalizeText(searchText || ""),
          }}
          onClear={onClear}
        />
      </div>
    );
  };

  // -- rendering

  return (
    <Select
      placeholder={isGraphFocusSearch ? t("debugger.focus_attribute") : t("debugger.find_attribute")}
      value={isGraphFocusSearch && !searchText ? focusedNodeId || null : null}
      options={selectOptions}
      onValueChange={selectNodeOnGraph}
      onInputChange={handleInputChange}
      inputValue={searchText || ""}
      clearable={true}
      onMenuClose={onClear}
      menuIsOpen={isStrNotNullOrBlank(searchText) && selectOptions.length > 0}
      noOptionsMessage={() =>
        searchResults === null
          ? isGraphFocusSearch
            ? t("debugger.focus_attribute")
            : t("debugger.find_attribute")
          : t("no_results")
      }
      className="min-w-[20rem]"
      menuProps={{ style: { minWidth: "550px", padding: 0 } }}
      renderMenuItem={CustomOption}
    />
  );
};

export default SearchRuleGraphNodes;
